// Stationen der Wertschöpfungskette. Position = Lage auf der Insel, Builder = Modell.
import * as THREE from "three"
import { COLORS } from "./palette.js"
import {
  at, group, box, cylinder, hall, chimney, silo, tree, roundTree, truck, car, house, windTurbine, pylon, plane,
  container, crate, cow, heap, fieldPatch, prism, mat
} from "./builders.js"

function withHooks(g) {
  g.userData.smoke ??= []
  g.userData.roofs ??= []
  g.userData.spots ??= []
  return g
}

function smokeFrom(g, chimneyObject, x, z) {
  g.add(at(chimneyObject, x, 0, z))
  g.userData.smoke.push(new THREE.Vector3(x, chimneyObject.userData.smokeTop.y, z))
}

const STATIONS = {
  power_plant: {
    position: [-8, -40],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(box(6, 3, 4, COLORS.wallWarm))
      g.add(at(box(6.2, 0.2, 4.2, COLORS.roof), 0, 3, 0))
      const tower = at(cylinder(1.6, 2.3, 5.5, COLORS.concrete, 12), -5.5, 0, 0)
      g.add(tower)
      g.userData.smoke.push(new THREE.Vector3(-5.5, 5.7, 0))
      smokeFrom(g, chimney(8, 0.5), 4, -1)
      g.userData.windSpots = [[8, -3], [11, 1], [8, 4.5]]
      return g
    }
  },
  steel_mill: {
    position: [-38, -14],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(hall({ w: 9, d: 6, h: 4.2, wall: COLORS.steel, roof: COLORS.steelDark }))
      g.add(at(cylinder(1.4, 1.8, 7, COLORS.rust, 10), -6.5, 0, 0))
      g.add(at(box(2.5, 0.4, 0.4, COLORS.steelDark), -4.5, 6, 0))
      smokeFrom(g, chimney(9, 0.55), 5.5, -2)
      smokeFrom(g, chimney(8, 0.5), 5.5, 1.5)
      for (let i = 0; i < 3; i++) g.add(at(box(2.4, 0.5, 1, COLORS.steelDark), -2 + i * 2.6, 0, 4.5))
      g.userData.glow = at(box(1.2, 1.2, 0.1, COLORS.orange, { emissive: COLORS.orange, emissiveIntensity: 0.9 }), -6.5, 1.2, 1.85)
      g.add(g.userData.glow)
      return g
    }
  },
  chem_plant: {
    position: [-31, 9],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(hall({ w: 7, d: 5, h: 3.2 }))
      for (let i = 0; i < 3; i++) g.add(at(silo(5.5, 0.9, COLORS.white), -6 + i * 2, 0, -1))
      g.add(at(cylinder(1.3, 1.3, 2.4, COLORS.green, 10), 5.5, 0, -1))
      g.add(at(box(9, 0.2, 0.2, COLORS.steel), -1, 4.5, -1))
      smokeFrom(g, chimney(7, 0.45), 5.5, 2.5)
      for (let i = 0; i < 3; i++) g.add(at(box(1.1, 0.9, 1.1, COLORS.wallWarm), -1 + i * 1.3, 0, 4))
      return g
    }
  },
  component_works: {
    position: [-20, -32],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(hall({ w: 8, d: 6, h: 3.2, sawtooth: 4, wall: COLORS.wallWarm }))
      g.add(at(hall({ w: 5, d: 4, h: 2.6, roof: COLORS.roofLight }), 8, 0, 1))
      smokeFrom(g, chimney(5, 0.4), -5.5, -2)
      for (let i = 0; i < 4; i++) g.add(at(crate(i % 2 ? COLORS.green : COLORS.wallWarm), -3 + i, 0, 4.5))
      g.userData.flagSpots = [[-4, 4], [4, -4], [10, 4]]
      return g
    }
  },
  farm: {
    position: [-46, 32],
    build() {
      const g = withHooks(new THREE.Group())
      const barn = group(box(4.5, 2.6, 3.4, COLORS.brick))
      barn.add(at(prism(5, 1.8, 3.8, COLORS.roof), 0, 2.6, 0))
      g.add(at(barn, -3, 0, -2))
      g.add(at(silo(5, 0.9, COLORS.steel), 1.6, 0, -2.8))
      const fields = [[-1, 5, 7, 3.5, COLORS.field], [7, 3, 4, 7, COLORS.fieldGreen], [-8, 4, 4, 5, COLORS.fieldGreen]]
      g.userData.fields = fields.map(([x, z, w, d, color]) => {
        const f = at(fieldPatch(w, d, color), x, 0, z)
        g.add(f)
        return f
      })
      for (const [x, z] of [[4, -1], [5.5, -2.2], [6, 0]]) g.add(at(cow(), x, 0, z, Math.random() * Math.PI))
      g.userData.smoke.push(new THREE.Vector3(5, 1.2, -1))
      return g
    }
  },
  quarry: {
    position: [-48, -2],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(at(cylinder(1.8, 1.8, 4, COLORS.concrete, 10), -2, 0, -1.5))
      g.add(at(box(8, 1.4, 1.2, COLORS.steel), 2, 4, -1.5))
      g.children.at(-1).rotation.z = 0.25
      g.add(at(hall({ w: 5, d: 4, h: 3, wall: COLORS.concrete, roof: COLORS.steelDark }), 5, 0, -1))
      smokeFrom(g, chimney(8, 0.5), -5, -2)
      g.userData.heaps = [at(heap(1.8, COLORS.gravel), -3, 0, 3.5), at(heap(1.4, COLORS.concrete), 1, 0, 4)]
      g.userData.heaps.forEach((h) => g.add(h))
      return g
    }
  },
  office_park: {
    position: [16, -34],
    build() {
      const g = withHooks(new THREE.Group())
      for (const [x, z, h] of [[-3, 0, 6], [2.5, -1, 8], [6.5, 1.5, 4.5]]) {
        g.add(at(box(3.4, h, 3.4, COLORS.wall), x, 0, z))
        for (let f = 0; f < Math.floor(h / 1.4); f++) g.add(at(box(3.46, 0.45, 3.46, COLORS.glass), x, 0.8 + f * 1.4, z))
        g.userData.roofs.push({ x, z, y: h, w: 3.4, d: 3.4 })
      }
      g.add(at(box(1.6, 1.2, 2.4, COLORS.navy600), -6.5, 0, 0))
      g.userData.smoke.push(new THREE.Vector3(-6.5, 1.4, 0))
      return g
    }
  },
  logistics_hub: {
    position: [12, 22],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(hall({ w: 9, d: 5, h: 3.4, wall: COLORS.wallWarm }))
      for (let i = 0; i < 3; i++) g.add(at(truck(COLORS.wall, COLORS.red), -3 + i * 3, 0, 4.5, Math.PI / 2))
      g.add(at(container(COLORS.green), -7.2, 0, -1))
      g.add(at(container(COLORS.navy600), -7.2, 1, -1))
      g.add(at(container(COLORS.rust), -7.2, 0, 1.2))
      g.userData.smoke.push(new THREE.Vector3(0, 1.2, 6))
      return g
    }
  },
  airport: {
    position: [40, -26],
    // Zufahrt endet am Parkplatz vor dem Terminal, die Landebahn liegt auf der abgewandten Seite
    entrance: [-8.5, 3],
    trees: false,
    build() {
      const g = withHooks(new THREE.Group())
      const flat = (mesh) => {
        mesh.castShadow = false
        mesh.userData.keepFree = true
        return mesh
      }

      // Landebahn entlang z mit Mittellinie, Schwellen und Randfeuern
      g.add(flat(at(box(3.4, 0.08, 22, COLORS.road), 6.5, 0, 0)))
      for (let z = -8; z <= 8; z += 2.6) g.add(flat(at(box(0.18, 0.1, 1.3, COLORS.roadLine), 6.5, 0.01, z)))
      for (const end of [-10.2, 10.2]) {
        for (let i = 0; i < 4; i++) g.add(flat(at(box(0.3, 0.1, 1, COLORS.roadLine), 5.3 + i * 0.8, 0.01, end)))
      }
      const lights = []
      for (let z = -10; z <= 10; z += 2.5) {
        for (const x of [4.7, 8.3]) {
          const light = at(box(0.18, 0.18, 0.18, COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 1 }), x, 0, z)
          light.castShadow = false
          lights.push(light)
          g.add(light)
        }
      }

      // Rollweg und Vorfeld
      g.add(flat(at(box(3.4, 0.07, 2.2, COLORS.road), 3.4, 0, -2)))
      g.add(flat(at(box(5.2, 0.06, 9, COLORS.concrete), -0.4, 0, -1)))

      // Terminal mit Fensterband und Fluggastbrücke
      g.add(at(box(3.2, 2.6, 8.5, COLORS.wall), -4.6, 0, -1))
      g.add(at(box(3.26, 0.7, 8.56, COLORS.glass), -4.6, 1.2, -1))
      g.add(at(box(3.5, 0.2, 8.8, COLORS.roof), -4.6, 2.6, -1))
      g.add(at(box(2.2, 0.6, 0.8, COLORS.steel), -2, 1.3, 1.2))

      // Geparktes Flugzeug am Gate
      const parked = at(plane(COLORS.white), 0.8, 0.45, 1.2)
      parked.scale.setScalar(0.85)
      g.add(parked)
      for (const [x, z] of [[1.8, 1.2], [-0.4, 0.9], [-0.4, 1.5]]) g.add(at(box(0.12, 0.45, 0.12, COLORS.navy), x, 0, z))

      // Tower mit drehendem Radar
      g.add(at(cylinder(0.55, 0.75, 6.5, COLORS.wall, 8), -4.4, 0, -7.2))
      g.add(at(cylinder(1.1, 0.9, 1.1, COLORS.glass, 8), -4.4, 6.5, -7.2))
      g.add(at(cylinder(1.25, 1.25, 0.2, COLORS.roof, 8), -4.4, 7.6, -7.2))
      const radar = new THREE.Group()
      radar.position.set(-4.4, 8.1, -7.2)
      radar.add(at(box(1.4, 0.35, 0.1, COLORS.white), 0, 0.2, 0))
      radar.add(at(box(0.1, 0.4, 0.1, COLORS.steelDark), 0, -0.2, 0))
      g.add(radar)

      // Hangar (halber Zylinder)
      // Halbzylinder: erst die runde Seite nach oben drehen, dann die Achse entlang z legen
      const hangarGeometry = new THREE.CylinderGeometry(2.4, 2.4, 5, 12, 1, false, 0, Math.PI).rotateZ(Math.PI / 2).rotateY(Math.PI / 2)
      const hangar = new THREE.Mesh(hangarGeometry, mat(COLORS.steel, { side: THREE.DoubleSide }))
      hangar.position.set(-2, 0, 7.6)
      hangar.castShadow = true
      hangar.receiveShadow = true
      g.add(hangar)
      g.add(at(box(3.2, 2.1, 0.1, COLORS.steelDark), -2, 0, 5.08))

      // Parkplatz mit Autos
      g.add(flat(at(box(3.6, 0.05, 5, COLORS.road), -8.5, 0, 2.5)))
      for (const [z, color] of [[1, COLORS.red], [2.6, COLORS.slate], [4.2, COLORS.white]]) g.add(at(car(color), -9.2, 0, z))

      // Windsack
      g.add(at(cylinder(0.06, 0.06, 3, COLORS.white, 6), 9.6, 0, 9))
      const sock = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.32, 1.3, 8, 1, true), mat(COLORS.orange, { side: THREE.DoubleSide }))
      sock.rotation.z = Math.PI / 2
      const sockPivot = new THREE.Group()
      sockPivot.position.set(9.6, 2.85, 9)
      sock.position.x = -0.7
      sockPivot.add(sock)
      g.add(sockPivot)

      // Gepäckwagenzug auf dem Vorfeld
      const baggage = new THREE.Group()
      baggage.add(at(box(0.8, 0.5, 0.6, COLORS.lime), 0, 0.1, 0))
      for (let i = 1; i <= 2; i++) baggage.add(at(box(0.8, 0.45, 0.6, COLORS.steel), -i * 1, 0.1, 0), at(box(0.6, 0.3, 0.45, 0xc9a46b), -i * 1, 0.55, 0))
      baggage.position.set(0, 0, -4.2)
      g.add(baggage)

      g.userData.runway = { x: 6.5, y: 0.4, from: -9, to: 9 }
      g.userData.animate = (time) => {
        radar.rotation.y = time * 1.5
        sockPivot.rotation.y = Math.sin(time * 0.4) * 0.5
        sock.rotation.x = Math.sin(time * 3) * 0.08
        lights.forEach((light, i) => light.scale.setScalar((Math.floor(time * 2) + i) % 4 === 0 ? 1.6 : 1))
        baggage.position.x = Math.sin(time * 0.35) * 1.4
        baggage.rotation.y = Math.cos(time * 0.35) > 0 ? 0 : Math.PI
      }
      return g
    }
  },
  suburb: {
    position: [-12, 38],
    build() {
      const g = withHooks(new THREE.Group())
      const houses = [[-5, -2], [-2, -2.4], [1, -2], [4, -2.2], [-4, 2.5], [-1, 2.8], [2.5, 2.4]]
      houses.forEach(([x, z], i) => g.add(at(house(i % 2 ? COLORS.wallWarm : COLORS.wall, i % 3 ? COLORS.brick : COLORS.roof), x, 0, z, i % 2 ? 0.1 : -0.1)))
      for (const [x, z] of [[-7, 0.5], [6.5, 0.5], [6, -3.5]]) g.add(at(roundTree(1), x, 0, z))
      g.userData.smoke.push(new THREE.Vector3(0, 1, 0))
      return g
    }
  },
  customers: {
    position: [40, 6],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(hall({ w: 9, d: 6, h: 3.6, sawtooth: 3, wall: COLORS.wallWarm }))
      g.add(at(box(4, 6, 4, COLORS.wall), -7.5, 0, -1))
      for (let f = 0; f < 4; f++) g.add(at(box(4.06, 0.45, 4.06, COLORS.glass), -7.5, 0.9 + f * 1.3, -1))
      // Produkte in Nutzung: Maschinen mit Statusleuchte
      g.userData.products = [0, 1, 2].map((i) => {
        const machine = at(box(1.5, 1.4, 1.2, COLORS.steel), -2.5 + i * 2.5, 0, 4.6)
        const light = at(box(0.3, 0.3, 0.3, COLORS.red, { emissive: COLORS.red, emissiveIntensity: 0.6 }), 0, 1.4, 0)
        machine.add(light)
        machine.userData.light = light
        g.add(machine)
        return machine
      })
      g.userData.smoke.push(new THREE.Vector3(0, 2, 4.5))
      return g
    }
  },
  recycling: {
    position: [34, 34],
    build() {
      const g = withHooks(new THREE.Group())
      g.add(at(hall({ w: 6, d: 4, h: 3, wall: COLORS.green, roof: COLORS.roof }), -2, 0, -1.5))
      for (const [x, z, color] of [[-4, 3, COLORS.steel], [-1.5, 3.4, COLORS.wallWarm], [1, 3, COLORS.slate]]) g.add(at(heap(1.1, color), x, 0, z))
      for (let i = 0; i < 3; i++) g.add(at(box(1.4, 1, 1, [COLORS.orange, COLORS.navy600, COLORS.green][i]), 4, 0, -2.5 + i * 1.6))
      smokeFrom(g, chimney(5, 0.4), 4.8, 3.5)
      return g
    }
  }
}

export const STATION_LAYOUT = Object.fromEntries(Object.entries(STATIONS).map(([id, s]) => [id, s.position]))
// Endpunkt der Zufahrt relativ zur Station (Standard: Mitte)
export const STATION_ENTRANCES = Object.fromEntries(Object.entries(STATIONS).map(([id, s]) => [id, s.entrance || [0, 0]]))

export function buildStation(id) {
  const station = STATIONS[id]
  if (!station) return null
  const g = station.build()
  g.position.set(station.position[0], 0, station.position[1])
  // Bäume rund um jede Station
  for (let i = 0; i < (station.trees === false ? 0 : 3); i++) {
    const angle = (i / 3) * Math.PI * 2 + id.length
    g.add(at(tree(0.9 + (i % 2) * 0.3), Math.cos(angle) * 10, 0, Math.sin(angle) * 8))
  }
  return g
}
